/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Counter, HelpTip } from 'sarsaparilla';
import { today } from '@internationalized/date';
import { durationLength } from '../utils/duration_utils';
import { getPrice } from '../utils';
import { PassTypeProps } from '../props';
import { getValidPriceFromDivisionV2, parseDate } from '../utils/date_time_utils';

export default class PassTypesTable extends PureComponent {
    static defaultProps = {
        pricing: null,
        passCounts: {},
        sortFn: (a, b) =>
            parseFloat(getPrice(b.periods).replace(/\/.*|\D/g, ' ')) -
            parseFloat(getPrice(a.periods).replace(/\/.*|\D/g, ' ')),
    };

    static propTypes = {
        maxTotalGuestsReached: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        passCounts: PropTypes.shape({
            [PropTypes.string]: PropTypes.number,
        }),
        passTypeId: PropTypes.string,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        pricing: PropTypes.node,
        sortFn: PropTypes.func,
    };

    getPriceFromPeriods = (periods) => {
        const price = getValidPriceFromDivisionV2(periods, today());
        return isEmpty(price) ? '0' : price;
    };

    filterPassTypes = (passTypes) => {
        return passTypes.filter((item) => {
            const currentDate = today();
            return item.periods.some(
                (period) => currentDate.compare(parseDate(period.start_date)) >= 0
            );
        });
    };

    renderRow = ({ id, name, periods, used_for: usedFor, duration, features }) => {
        const { maxTotalGuestsReached, passCounts, onChange } = this.props;
        let disableByQuota = false;
        if (features?.pass_type_limit?.has_limit) {
            const available =
                (features?.pass_type_limit?.limit ?? 0) -
                (features?.pass_type_limit?.count ?? 0);
            if (passCounts[id]) {
                const updateAvailable = available - passCounts[id];
                if (updateAvailable <= 0) {
                    disableByQuota = true;
                }
            }
        }

        const price = this.getPriceFromPeriods(periods);
        const durationValue = durationLength(duration);
        return (
            <tr
                key={`pass-type-${id}`}
                className={this.props.passTypeId === id ? 'highlight' : ''}
            >
                <td>
                    {name}
                    {usedFor ? (
                        <HelpTip tipForDescriptor={name} content={usedFor} />
                    ) : null}

                    <small className="mobile-only">{price}</small>
                </td>
                <td>{durationValue}</td>
                <td>{price}</td>
                <td>
                    <div className="rec-guest-counter-wrap">
                        <div className="rec-guest-counter-row">
                            <Counter
                                disableDecreaseCount={passCounts[id] <= 0}
                                disableIncreaseCount={
                                    maxTotalGuestsReached ||
                                    durationValue === 'Not Valid' ||
                                    disableByQuota
                                }
                                disableTextInput
                                onCountChange={(value) => onChange(id, value)}
                                count={passCounts[id]}
                                label={name}
                                iconClass="guest-counter-row-icon"
                                inputClass="rec-guest-counter-row-quantity"
                            />
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    render() {
        let { passTypes } = this.props;
        const { pricing, sortFn } = this.props;

        if (passTypes?.length > 0) {
            passTypes = this.filterPassTypes(passTypes);
        }

        return (
            <div className="activitypass-pass-types-table-wrapper">
                <table className="activitypass-pass-types-table">
                    <caption className="rec-sr-only">
                        Activity pass options for purchase
                    </caption>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Duration</th>
                            <th>Price</th>
                            <th>Quantity</th>
                        </tr>
                    </thead>
                    <tbody>{passTypes.sort(sortFn).map(this.renderRow)}</tbody>
                </table>
                <div aria-live="polite">{pricing}</div>
            </div>
        );
    }
}

// cspell:ignore activitypass
